import { Communication, CommunicationSearch, SearchCommunicationResponseCommunicationConnectionArgs } from '@/models/gen/graphql';
import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchCommunicationsDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchCommunications.communicationConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Communications' } });

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof CommunicationSearch]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<Communication>>;
}

// SearchCommunications Service
export const { searchCommunications, refetchCommunications, useSearchCommunications } = ((): {
  searchCommunications: ServiceMethod;
  refetchCommunications: ServiceMethod;
  useSearchCommunications: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchCommunicationsDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<Communication>> => {
        const args: SearchCommunicationResponseCommunicationConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: payload,
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchCommunications: fetch,
    refetchCommunications: refetch,
    useSearchCommunications: useService,
  };
})();
